import type {SearchFormPageData, SearchStringsState} from './types';
import type {Criterion, CriterionField, OperatorableCriterion} from '../search-criteria/criteria.types';
import type {CriterionsFormManagerState} from '../search-criteria/criterions-form-manager';
import type {KpSearchFormService} from './kp-search-form.service';
import type {Observable, Subject} from 'rxjs';
import {BehaviorSubject} from 'rxjs';
import {exists, identifiedValuesEquals} from 'shared/utils/custom-utils';
import {CRITERION_OPERATORS} from '../search-criteria/criterions/criterion-operators';
import {transferify} from 'shared/utils/data-service-utils';
import CriterionsFormManager from '../search-criteria/criterions-form-manager';
import OperatorableCriterionFactory from '../search-criteria/criterion-factories/operatorable-criterion.factory';
import CriterionFactory from '../search-criteria/criterion-factories/criterion.factory';
import OperatorableCriterionMarshaller from '../search-criteria/criterion-marshallers/operatorable-criterion-marshaller';

export class KpSearchFormManager {

    public criterionsFormManager: CriterionsFormManager;
    public operatorableCriterionMarshaller: OperatorableCriterionMarshaller;

    private readonly stringsState$: Subject<SearchStringsState>;

    constructor(public readonly pageData: SearchFormPageData,
                private service: KpSearchFormService) {

        const operatorableCriterionFactory = new OperatorableCriterionFactory(new CriterionFactory());
        this.criterionsFormManager = new CriterionsFormManager(operatorableCriterionFactory);
        this.operatorableCriterionMarshaller = new OperatorableCriterionMarshaller(operatorableCriterionFactory);
        this.stringsState$ = new BehaviorSubject({
            restrictionString: '',
            luceneQueryString: ''
        });
    }

    public async initialize(): Promise<void> {
        this.fillCriterions(false);

        if (this.pageData.searchParametersHistoryEnabled) {
            const searchHistory = await this.service.searchHistoryDataService.getAll();
            const firstImportableSearch = searchHistory.find((item) => exists(item.qt));

            if (exists(firstImportableSearch)) {
                this.useRestriction(firstImportableSearch.qt);
            }
        }
    }

    public fillCriterions(expertMode: boolean): void {
        if (expertMode) {
            this.criterionsFormManager.setCriterions(this.criterionsFormManager.getNotEmptyCriterions());
            if (this.criterionsFormManager.getCriterions().length === 0) {
                this.addCriterion();
            }

            return;
        }

        const criterionsFromSearchFields = this.pageData.searchFields.map((field) => this.criterionsFormManager.createCriterionByField(field));
        const prefilledCriterions = this.criterionsFormManager.getCriterions();

        const mergedCriterions = criterionsFromSearchFields.map((criterion) => {
            for (const prefilledCriterion of prefilledCriterions) {
                if (identifiedValuesEquals(criterion.field, prefilledCriterion.field)) {
                    return prefilledCriterion;
                }
            }

            return criterion;
        });

        const criterions = mergedCriterions.slice(0, this.pageData.basicModeFieldsCount);
        this.criterionsFormManager.setCriterions(criterions);
    }

    public async resetForm(expertSearch: boolean): Promise<void> {
        this.criterionsFormManager.clearCriterions();
        this.fillCriterions(expertSearch);
        await this.buildQueryIfAdmin();
    }

    public getCriterionsState$(): Observable<CriterionsFormManagerState> {
        return this.criterionsFormManager.getState$();
    }

    public getSearchStringsState$(): Observable<SearchStringsState> {
        return this.stringsState$.asObservable();
    }

    public async addCriterion(field?: any): Promise<void> {
        this.criterionsFormManager.addCriterion(field ?? this.pageData.searchFields[0]);
        await this.buildQueryIfAdmin();
    }

    public async removeCriterion(criterion: Criterion): Promise<void> {
        this.criterionsFormManager.removeCriterion(criterion);
        await this.buildQueryIfAdmin();
    }

    public get isAdministrator(): boolean {
        return this.service.currentAuth.role.includes('ROLE_ADMIN');
    }

    public async updateCriterionByField(criterion: Criterion, criterionField: CriterionField): Promise<void> {
        this.criterionsFormManager.updateCriterionByField(criterion, criterionField);
        await this.buildQueryIfAdmin();
    }

    public async updateCriterionValue(criterion: Criterion, newValue: any): Promise<void> {
        this.criterionsFormManager.updateCriterionValue(criterion, newValue);
        await this.buildQueryIfAdmin();
    }

    public async submitForm(isAnyCriterionFilled: boolean): Promise<void> {
        if (!isAnyCriterionFilled) {
            return;
        }

        const req = this.operatorableCriterionMarshaller.serialize(this.criterionsFormManager.getCriterions() as OperatorableCriterion[]);
        await this.service.walker.newPage('/search', {
            type: 'complex',
            qt: JSON.stringify(transferify(req))
        });
    }

    public useRestriction(restriction: any): void {
        this.criterionsFormManager.setCriterions(this.operatorableCriterionMarshaller.deserialize(restriction));
        this.fillCriterions(this.isExpertModeSearch());
    }

    public async buildQueryIfAdmin(): Promise<void> {
        if (this.isAdministrator) {
            await this.buildQuery();
        }
    }

    private async buildQuery(): Promise<void> {
        const req = this.operatorableCriterionMarshaller.serialize(this.criterionsFormManager.getCriterions() as OperatorableCriterion[]);
        const transferableReq = transferify(req);

        const restrictionString = JSON.stringify(transferableReq, null, 4);
        const luceneQueryString = await this.service.loadLuceneQuery(req);

        this.stringsState$.next({
            restrictionString,
            luceneQueryString
        });
    }

    private isExpertModeSearch() {
        const containsExpertSearchOperators = this.criterionsFormManager
            .getCriterions()
            .some(({operator}: OperatorableCriterion) => operator === CRITERION_OPERATORS.OR || operator === CRITERION_OPERATORS.AND_NOT);

        if (containsExpertSearchOperators) {
            return true;
        }

        const criterionFieldsIds = this.criterionsFormManager.getCriterions().map(({field}) => field.id);
        const containsDuplicitFields = new Set([...criterionFieldsIds]).size < criterionFieldsIds.length;

        if (containsDuplicitFields) {
            return true;
        }

        return false;
    }
}